"use client";

import { signedPct, trimDecimals } from "@/lib/format/change";
import type React from "react";

/**
 * Client half of the residence page (rebuilt 2026-11-01 after the korter.az
 * review, docs/research/korter-az-2026-11-01.md):
 *
 *   1. the top: the photo beside the facts a visitor came for (the AZN/m²,
 *      "from" prices for sale and for rent with their listing counts, the
 *      developer, built or under construction and when), the freshness of
 *      those numbers and the follow hook (the strip of jump links under it
 *      went 2026-11-01: the owner did not like it)
 *   2. the photo gallery and the advisories
 *   3. the AI card, once: under the top on phones, heading the side
 *      column on a computer (it used to be in the page twice)
 *   4. "{name} satılan mənzillər": count, ortalama, prices by rooms and the
 *      cards (the apartments a searcher came to see)
 *   5. "{name} mənzil planları": floor plans from the listings' own photos,
 *      right under the sale cards (owner, 2026-11-01: higher up), then the
 *      good-value listings around it
 *   6. "{name} kirayə mənzillər": the same for rent, with the rent trend
 *   7. "{name} mənzil qiymətləri": the district, the segments, renovation
 *      and the sale trend
 *   8. "{name} haqqında": every catalogue fact and the residence in words
 *      (it used to be two sections, "Rezidensiya haqqında" and "Kompleks
 *      haqqında")
 *   9. FAQ (same strings as the FAQPage JSON-LD emitted by page.tsx),
 *      comments, then the server-rendered searches
 *
 * The section headings are worded the way people search ("Belvedere
 * Residence kirayə mənzillər"), and each has an anchor (the top's listing
 * counts glide to the sale and rent sections).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import Link from "@/lib/localized-link";
import { mediaSrc, API_BASE, mediaThumb } from "@/lib/api";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";
import SharedHeader from "@/components/SharedHeader";
import FaqList from "@/components/FaqList";
import dynamic from "next/dynamic";
import WhenVisible from "@/components/WhenVisible";
import {
  Archive, ArrowDownRight, ArrowUpDown, ArrowUpRight, Baby, Bike, Building2, Car, Cctv, ChevronRight, Clapperboard,
  ConciergeBell, Dumbbell, Droplets, Fence, Flame, KeyRound, MapPin, Minus, PlugZap, School, Shapes, Shield,
  ShoppingCart, SquareParking, Stethoscope, Store, Sun, ThermometerSun, TreePalm, Trees, Trophy, UtensilsCrossed, Waves, Zap,
  type LucideIcon,
} from "lucide-react";
import AdvisoryBanner from "@/components/AdvisoryBanner";
import OfficialLinks from "@/components/OfficialLinks";
import SectionExpander from "@/components/residences/SectionExpander";
import RenovationDonut from "@/components/residences/RenovationDonut";
import ResidenceComments from "@/components/residence/ResidenceComments";
import DeveloperLogo from "@/components/developers/DeveloperLogo";
import FollowResidenceButton from "@/components/residence/FollowResidenceButton";
import { followViaAlert } from "@/components/residence/useResidenceFollow";
import { useAuth } from "@/lib/auth-context";
import {
  useResidenceFollow,
} from "@/components/residence/useResidenceFollow";
import { trackEvent } from "@/lib/tracking";
import { useStickyRail } from "@/lib/use-sticky-rail";
import ListingBrowseCard from "@/components/listings/ListingBrowseCard";
import NearbyDeals from "@/components/listings/NearbyDeals";
import { withDetailBody } from "@/components/listings/detail-body-loader";
import PreviousListings from "@/components/residence/PreviousListings";
import ResidenceGallery, { fullPhoto } from "@/components/residence/ResidenceGallery";
import PhotoLightbox from "@/components/result/PhotoLightbox";
import { createPortal } from "react-dom";
import ResidencePlans from "@/components/residence/ResidencePlans";
import FreshLine from "@/components/residence/FreshLine";
import { glideToSection } from "@/lib/glide-to-section";
import { listingPath } from "@/lib/seo/paths";
import { num } from "@/lib/seo/format";
import { localizeCatalogValue } from "@/lib/catalog-values";
import Illo from "@/components/Illo";
import { placeholderFor } from "@/components/residences/ResidenceImage";
import { encodePrefill } from "@/lib/filter-presets";
import { buildState, pluralKey, type PlanTile } from "@/lib/residence-top";
import type { ListingCard, ListingsResponse } from "@/types";
import type { ListingFacts, ResidenceSummary } from "./page";
import InterestCard from "@/components/demand/InterestCard";
import type { FaqItem } from "./faq";
import Photo from "@/components/Photo";
import { BUTTON_SECONDARY, CHEVRON, INLINE_LINK, LINK, SELECTED } from "@/lib/ui-classes";

// Below-the-fold weekly trend chart - lazy so the chart bundle stays out
// of the residence page's initial load (the headline numbers are in the
// server HTML, so this isn't SEO-critical). Mounted by WhenVisible: the
// dynamic import alone still loaded ApexCharts right after hydration.
const TrendChart = dynamic(() => import("@/components/TrendChart"), {
  ssr: false,
  loading: () => <div className="h-48 rounded-xl bg-gray-50 animate-pulse" />,
});

// Live detail drawer (same one /map uses, overlay variant). Interaction-
// only + heavy (carousel, mini-map), so it's lazy and mounts only after
// the first card click - the glance cards themselves stay in the server
// HTML. Its content chunk is fetched alongside the frame's.
const ListingDetailPanel = dynamic(
  () => withDetailBody(import("@/components/listings/ListingDetailPanel")),
  { ssr: false },
);

function fmt(n: number | null | undefined): string {
  if (!n) return "-";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

const NBSP = String.fromCharCode(0xa0);

/** "375 000 AZN" that never breaks inside (DESIGN.md: no-break thousands). */
function money(n: number): string {
  return `${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)}${NBSP}AZN`;
}

// The page's two heading sizes: a section (the anchors' targets) and a
// block inside one. scroll-mt keeps a jumped-to heading clear of the
// sticky header.
const SECTION = "mt-10 scroll-mt-24";
// The sections under the sale cards are not drawn until they come near
// the screen (cv-auto, as on the area landings): on a phone their card
// and plan photos used to start loading with the page, next to the top's
// photo. The sale section itself stays drawn: it is the second screen,
// and skipping it pulled the rent cards into the loading range instead.
const SECTION_LATER = `${SECTION} cv-auto`;
const H2 = "text-lg font-bold tracking-tight text-gray-900";
const H3 = "mb-3 text-sm font-semibold text-gray-900";

/** The amenity kinds' icons (lucide, grey, sized to the text line). */
const AMENITY_ICONS: Record<string, LucideIcon> = {
  has_24h_security: Shield, has_cctv: Cctv, has_coded_entry: KeyRound, has_concierge: ConciergeBell,
  has_closed_courtyard: Fence, parking_underground: SquareParking, parking_surface: Car, has_playground: Shapes,
  has_park_inside: Trees, has_sports_courts: Trophy, has_jogging_bike_paths: Bike, has_gym: Dumbbell, has_pool: Waves,
  has_spa_sauna: ThermometerSun, has_kindergarten: Baby, has_kids_club: Baby, has_school: School,
  has_supermarket_inside: ShoppingCart, has_shops_ground_floor: Store, has_cafe_inside: UtensilsCrossed,
  has_medical_point: Stethoscope, has_generator: Zap, has_fire_safety_system: Flame, has_beach_access: TreePalm,
  has_roof_terrace: Sun, has_cinema_hall: Clapperboard, has_ev_charging: PlugZap, has_water_filtration: Droplets,
  has_storage_rooms: Archive,
};

/** "0,5" in az / ru, "0.5" in en; whole numbers stay whole. */
function decimal(n: number, locale: string): string {
  const s = Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  return locale === "en" ? s : s.replace(".", ",");
}

/** The top photo, a button into the gallery viewer when there are photos
 *  to view (Enter / Space through the native button); the photo itself
 *  paints exactly as before (no fade, so it still counts as the LCP). */
function HeroFrame({ onOpen, label, children }: { onOpen: (() => void) | null; label: string; children: React.ReactNode }) {
  if (!onOpen) return <>{children}</>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className="block h-full w-full cursor-zoom-in rounded-2xl sm:min-h-72 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
    >
      {children}
    </button>
  );
}

const SEGMENT_ORDER = [
  "rooms_1", "rooms_2", "rooms_3", "rooms_4plus",
  "with_repair", "no_repair", "first_floor", "last_floor",
] as const;


/** Insight fragments ("Label: value | Label: value") as prominent stat
 *  chips - the section's takeaway must be impossible to miss, not a
 *  small gray sentence. A string without separators renders as one
 *  emphasized line. */
function InsightChips({ text }: { text: string }) {
  const parts = text.split(" | ").map((x) => x.trim()).filter(Boolean);
  if (parts.length <= 1) {
    return (
      <p className="text-[15px] font-semibold text-gray-900 leading-snug tabular-nums">
        {text}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {parts.map((part) => {
        const i = part.indexOf(":");
        const label = i > 0 ? part.slice(0, i).trim() : null;
        const value = i > 0 ? part.slice(i + 1).trim() : part;
        return (
          <span
            key={part}
            className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5"
          >
            {label && (
              <span className="block text-xs font-semibold text-gray-500">
                {label}
              </span>
            )}
            <span className="block text-sm font-bold text-gray-900 tabular-nums">
              {value}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** The one line under a sale or rent heading: parts joined by " · ", each
 *  kept whole on a narrow phone, plus an optional chip. */
function SummaryLine({ parts, chip }: { parts: Array<string | null>; chip?: React.ReactNode }) {
  const shown = parts.filter((p): p is string => Boolean(p));
  if (!shown.length && !chip) return null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm tabular-nums text-gray-600">
      {shown.length > 0 && (
        <span>
          {shown.map((p, i) => (
            <span key={p}>
              {i > 0 && " · "}
              <span className="whitespace-nowrap">{p}</span>
            </span>
          ))}
        </span>
      )}
      {chip}
    </p>
  );
}

export default function ResidencePageClient({
  locale,
  summary,
  faq,
  listings,
  rentListings,
  plans,
  plansTotal = 0,
  previous,
  fresh,
  about,
  children,
  displayName,
  h1Tail,
  heroAlt,
  akaNames,
  roomsSale,
  roomsRent,
  location,
}: {
  locale: Locale;
  summary: ResidenceSummary;
  faq: FaqItem[];
  /** The spelling people search (the title leads with it); h1Tail is the
   *  h1's second line, matching the title's intent (lib/seo/residence-snippet). */
  displayName?: string;
  h1Tail?: string;
  heroAlt?: string;
  /** Other names fit to print on this locale's page (no Cyrillic on az,
   *  no company labels or capitals); falls back to display_aliases. */
  akaNames?: string[];
  /** Server-rendered per-room price tables, one inside each of the sale
   *  and rent sections (RoomsTable only="sale" / only="rent"). */
  roomsSale?: React.ReactNode;
  roomsRent?: React.ReactNode;
  /** Server-rendered location block, at the top of the side rail. */
  location?: React.ReactNode;
  /** The residence in words (server-rendered paragraphs); it closes the
   *  page's one about section. */
  about?: React.ReactNode;
  /** The Baku day the page read its data and that day in words, for the
   *  freshness line under the facts. */
  fresh: { day: string; label: string };
  /** Server-rendered extras after the main column: the bridge into
   *  this residence's area searches and comparisons. Passed as children
   *  so they stay server components with crawlable links. */
  children?: React.ReactNode;
  /** First page of active for-sale listings in this residence, fetched
   *  server-side by page.tsx (null when none / fetch failed - the
   *  glance section simply doesn't render). */
  listings?: ListingsResponse | null;
  /** First page of active rentals here, for the rent section's cards. */
  rentListings?: ListingsResponse | null;
  /** Floor plans from the residence's plan archive, one per layout, the
   *  ones for sale first (lib/residence-top planTiles), and how many sale
   *  listings carry one now. */
  plans?: PlanTile<ListingCard>[];
  plansTotal?: number;
  /** The listings this residence had that are gone now (quiet page glance). */
  previous?: ListingsResponse | null;
}) {
  // Below-fold sections fade up as they enter the viewport (added
  // post-hydration only - SSR/no-JS keeps everything plainly visible).
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const sections = Array.from(document.querySelectorAll<HTMLElement>("main section"));
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    for (const el of sections) {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add("reveal-init");
        io.observe(el);
      }
    }
    return () => io.disconnect();
  }, []);

  // The side rail (lg+): taller than the window, it sticks by its bottom.
  const rail = useStickyRail<HTMLElement>();

  const t = (k: TranslationKey) => translate(k, locale);
  const fill = (s: string, map: Record<string, string | number>) =>
    Object.entries(map).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), s);

  const name = displayName ?? summary.name;
  const overall = summary.segments?.overall;
  const premium = summary.district.premium_pct;
  const segments = summary.segments || {};

  // Renovation impact: per-room renovated-vs-non-renovated AZN/m², shown only
  // for room buckets where BOTH cohorts cleared the builder's sample gate.
  // Mirrors the result page's room x repair split so the same separation a
  // listing is judged against is visible on the residence too.
  type RepairRow = { rb: string; wr: { m: number; n: number }; nr: { m: number; n: number } };
  const repairRows: RepairRow[] = (["rooms_1", "rooms_2", "rooms_3", "rooms_4plus"] as const)
    .map((rb): RepairRow | null => {
      const wr = segments[`${rb}_with_repair`];
      const nr = segments[`${rb}_no_repair`];
      return wr?.m && nr?.m ? { rb, wr, nr } : null;
    })
    .filter((r): r is RepairRow => r != null);
  const showRepairImpact = repairRows.length > 0;

  // Generic segment list. When the per-room renovation matrix is shown,
  // drop the all-rooms with/without-repair rows from it to avoid saying
  // the same thing twice.
  const segKeys = SEGMENT_ORDER.filter(
    (k) => segments[k]?.m && !(showRepairImpact && (k === "with_repair" || k === "no_repair")),
  );
  const intel = summary.intel;
  const c = summary.catalog;

  // --- Section insights (cognitive-load pass) ---------------------------
  // Each dense below-fold section leads with ONE sentence computed from
  // the data it already shows; the full table then sits behind a
  // server-rendered <details> expander (SectionExpander), so nothing
  // leaves the crawlable HTML but the visitor reads a conclusion, not a
  // wall of numbers.
  const segLabel = (k: string) =>
    translate(`residence.seg.${k}` as TranslationKey, locale).toLocaleLowerCase(locale);

  // Segments: name the priciest and the most affordable displayed
  // segment and the spread between them.
  let segInsight: string | null = null;
  if (segKeys.length >= 2) {
    const sorted = [...segKeys].sort((a, b) => segments[b].m - segments[a].m);
    const maxK = sorted[0];
    const minK = sorted[sorted.length - 1];
    const spread = Math.round((segments[maxK].m / segments[minK].m - 1) * 100);
    segInsight = spread >= 5
      ? fill(t("residence.segments.insight"), {
          max: segLabel(maxK), maxv: fmt(segments[maxK].m),
          min: segLabel(minK), minv: fmt(segments[minK].m),
          pct: spread,
        })
      : fill(t("residence.segments.insight.flat"), { pct: spread });
  }

  // Renovation impact: the average renovated-over-non-renovated uplift
  // across the room buckets the table shows.
  const repairAvgPct = showRepairImpact
    ? Math.round(
        (repairRows.reduce((acc, r) => acc + (r.wr.m / r.nr.m - 1), 0) / repairRows.length) * 100,
      )
    : 0;
  const repairInsight = !showRepairImpact
    ? null
    : repairAvgPct > 1
      ? fill(t("residence.repair.insight.above"), { pct: repairAvgPct })
      : repairAvgPct < -1
        ? fill(t("residence.repair.insight.below"), { pct: Math.abs(repairAvgPct) })
        : t("residence.repair.insight.flat");

  const saleN = summary.live.sale.n;
  const rentN = summary.live.rent.n;
  const dealsN = summary.live.sale.deals_n ?? 0;
  // Nothing on the market right now, sale or rent (the normal life of a
  // sold-out new-build). The page keeps the shape every other residence
  // has; the listings and the statistics are simply absent, and no
  // banner explains the state or dates an older one (owner, 2026-10-18).
  const quiet = saleN === 0 && rentN === 0;
  const newHere = intel?.complex_new_7d ?? 0;

  const browseHref = `/listings?complex_slug=${summary.cluster_id}`;
  const dealsHref = `${browseHref}&deal_tier=great&deal_tier=good`;
  const rentHref = `${browseHref}&leased=true`;

  // Listings at a glance: the server-fetched first page of active
  // for-sale cards. `total` comes from the same /api/listings query the
  // "see all" link opens, so the promised count always matches the
  // destination; the summary's (24h-cached) counter is only a fallback.
  const glanceCards = listings?.results ?? [];
  const glanceTotal = listings?.total ?? saleN;
  const rentCards = rentListings?.results ?? [];
  const rentTotal = rentListings?.total ?? rentN;
  const planList = plans ?? [];

  // Live detail drawer target (mirrors ListingsGrid's openDetail).
  const [activeListing, setActiveListing] = useState<{
    externalId: string;
    listingId: string;
    sourceUrl: string;
    lat: number | null;
    lng: number | null;
    overpricedPct: number | null;
    thumbnailUrl: string | null;
  } | null>(null);

  function openDetail(listing: ListingCard) {
    // external_id drives the live fetch; legacy rows without it can't
    // be live-fetched, so the click is a no-op for them.
    if (!listing.external_id) return;
    setActiveListing({
      externalId: listing.external_id,
      listingId: listing.id,
      sourceUrl: listing.source_url,
      lat: listing.latitude,
      lng: listing.longitude,
      overpricedPct: listing.ai_overpriced_pct,
      thumbnailUrl: listing.thumbnail_url ?? null,
    });
  }
  // The follow hook: one instance shared by the top's button and the AI
  // card so the two surfaces can never disagree. The intent-timed
  // FollowPrompt that used to hang off this hook was retired 2026-09-20:
  // two follows in two months against ~1,000 landing sessions a month,
  // and a quarter of dismissers left right after it (replay analysis).
  const followHook = useResidenceFollow(summary.cluster_id, locale);
  // The gallery viewer's open photo (shared by the top photo and the tiles).
  const [viewerAt, setViewerAt] = useState<number | null>(null);

  // Glance filters: rooms + sort re-query the same complex-scoped
  // /api/listings the section was server-fetched from. Defaults
  // short-circuit back to the server HTML cards (no fetch, no flicker).
  const [roomFilter, setRoomFilter] = useState<number | null>(null);
  const [glanceSort, setGlanceSort] = useState<"newest" | "price" | "price_desc">("newest");
  const [filtered, setFiltered] = useState<ListingsResponse | null>(null);
  const [filterBusy, setFilterBusy] = useState(false);
  const filtersActive = roomFilter !== null || glanceSort !== "newest";

  useEffect(() => {
    if (!filtersActive) {
      setFiltered(null);
      return;
    }
    let cancelled = false;
    setFilterBusy(true);
    const qs = new URLSearchParams({
      complex_slug: summary.cluster_id,
      leased: "false",
      sort: glanceSort,
      page: "1",
      page_size: "6",
    });
    // The backend matches rooms exactly (room_count IN), so "4+" fans
    // out to the realistic top of the range.
    if (roomFilter === 4) [4, 5, 6, 7].forEach((r) => qs.append("rooms", String(r)));
    else if (roomFilter !== null) qs.append("rooms", String(roomFilter));
    fetch(`${API_BASE}/listings?${qs.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: ListingsResponse | null) => {
        if (cancelled) return;
        setFiltered(d ?? { total: 0, results: [] } as unknown as ListingsResponse);
        setFilterBusy(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFiltered({ total: 0, results: [] } as unknown as ListingsResponse);
        setFilterBusy(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFilter, glanceSort, summary.cluster_id]);

  const displayCards = filtersActive && filtered ? (filtered.results ?? []) : glanceCards;
  const displayTotal = filtersActive && filtered ? (filtered.total ?? 0) : glanceTotal;

  function cycleSort() {
    setGlanceSort((s) =>
      s === "newest" ? "price" : s === "price" ? "price_desc" : "newest",
    );
  }

  // Alert seeded for THIS residence: a saved search on the complex so the
  // user gets pinged when a new listing appears here. The sharpest seed
  // there is (a single complex is never "too broad"), and the strongest
  // retention hook per the analytics. Mirrors the result page CTA.
  const alertHref = `/alerts/new?prefill=${encodePrefill({
    complex_slugs: [summary.cluster_id],
    geo_mode: "area",
  })}`;

  const { user: authUser, loading: authLoading } = useAuth();

  // Push cannot carry the follow (blocked, unsupported, refused): signed
  // in, one tap turns it into a saved search on this residence (Telegram
  // or e-mail); signed out, the Telegram bot's deep link (no account;
  // the sign-in sheet, which returns here with ?follow=1, is one link
  // away in the top's button). The alert form stays as the last resort
  // (no verified e-mail, or the account's alert cap).
  const followLadder = useCallback(async () => {
    if (!authUser) {
      trackEvent("residence.next.follow", { cluster_id: summary.cluster_id, step: "telegram" });
      followHook.followViaTelegram();
      return;
    }
    const res = await followViaAlert(summary.cluster_id);
    if (res.ok) {
      followHook.markFollowing();
      return;
    }
    if (res.reason === "email_not_verified" || res.reason === "limit") {
      window.location.href = `/${locale}${alertHref}`;
    }
  }, [authUser, summary.cluster_id, followHook, locale, alertHref]);

  // Back from sign-in with ?follow=1: finish what the tap started. Push
  // first when the browser allows it, the saved search otherwise; then
  // drop the flag from the URL so a reload does not repeat it.
  const followParamDone = useRef(false);
  useEffect(() => {
    if (followParamDone.current || authLoading || !authUser) return;
    const q = new URLSearchParams(window.location.search);
    if (q.get("follow") !== "1") return;
    followParamDone.current = true;
    q.delete("follow");
    const clean = `${window.location.pathname}${q.toString() ? `?${q}` : ""}`;
    window.history.replaceState(null, "", clean);
    (async () => {
      if (followHook.eligible && followHook.state === "idle") {
        const res = await followHook.follow();
        if (res.ok) return;
      }
      await followLadder();
    })();
  }, [authLoading, authUser, followHook, followLadder]);

  // Address opens Google Maps - prefer the catalog coordinates (drops a
  // pin on the exact spot); fall back to a text query when there are no
  // coords. The label stays the human address.
  const mapsHref = c.address_full
    ? (c.latitude != null && c.longitude != null
        ? `https://www.google.com/maps/search/?api=1&query=${c.latitude},${c.longitude}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.address_full}, Bakı`)}`)
    : null;

  // The admin hero photo, else the gallery's first outside shot (card
  // size), else the placeholder.
  const galleryFirst = summary.gallery?.[0]?.url.replace(/\/uploads\/(thumbnail|full)\//, "/uploads/f460x345/") ?? null;
  const heroSrc = summary.image_url ? mediaSrc(summary.image_url)! : galleryFirst;
  // The gallery: the researched profile's captioned photos (what each shows)
  // first, then the listings' outside shots; without an admin hero the
  // first outside shot IS the top's photo, so it is not repeated.
  const profile = summary.profile ?? null;
  const photoKey = (u: string) => u.replace(/\/uploads\/(thumbnail|full|f460x345)\//, "/uploads/");
  const profilePhotos = (profile?.photos ?? []).map((p) => ({ url: p.url, caption: p.caption }));
  const seenPhotos = new Set(profilePhotos.map((p) => photoKey(p.url)));
  if (!summary.image_url && summary.gallery?.[0]) seenPhotos.add(photoKey(summary.gallery[0].url));
  const galleryPhotos = [
    ...profilePhotos,
    ...(summary.gallery ?? []).filter((g) => !seenPhotos.has(photoKey(g.url))).map((g) => ({ url: g.url, caption: null })),
  ];
  // One viewer for the top photo and the gallery tiles (owner, 2026-11-01:
  // the top photo opens the gallery too). When the top photo is the first
  // outside shot it leads the viewer; an admin hero is not a gallery photo,
  // so a tap on it opens the gallery at its first photo.
  const heroInViewer = !summary.image_url && summary.gallery?.[0]
    ? [{ url: summary.gallery[0].url, caption: null as string | null }]
    : [];
  const viewerPhotos = [...heroInViewer, ...galleryPhotos];

  // The flats count is junk below ~50 (Korter's "Mənzillərin sayı" holds
  // 11 for Lux Residence's 812 flats, 4 for BCR Khatai), so a small one is
  // dropped.
  // The researched profile is checked against the sources; where its
  // floors, buildings or flats disagree with the catalogue's number (Lux
  // Residence: 14, 16 and 20 floors, the catalogue 16), the top drops the
  // catalogue's figure and the profile's row says it (2026-11-01).
  const profileNums = (key: string): number[] | null => {
    const v = profile?.facts.find((f) => f.key === key)?.value;
    return v ? (v.replace(/(\d)[\s\u00a0](?=\d{3}\b)/g, "$1").match(/\d+/g) ?? []).map(Number) : null;
  };
  const pFloors = profileNums("floors");
  const pBlocks = profileNums("blocks");
  const pFlats = profileNums("flats");
  const maxFloor = c.max_floor && (!pFloors?.length || Math.max(...pFloors) === c.max_floor) ? c.max_floor : null;
  const buildingCount = c.building_count && (!pBlocks?.length || pBlocks.includes(c.building_count)) ? c.building_count : null;
  // A flats count equal to the buildings count is a copy slip (Mayak
  // Residence: "59 bina · 59 mənzil").
  const flats = c.unit_count_max != null && c.unit_count_max >= 50 && c.unit_count_max !== c.building_count
    && (!pFlats?.length || pFlats.includes(c.unit_count_max))
    ? num(c.unit_count_max)
    : null;
  const devs = c.developers ?? [];
  // About rows: the catalogue facts the top does not already say (the
  // developer, built or under construction and when, floors, buildings
  // and flats lead the page now; 2026-11-01: say each fact once).
  const lf: ListingFacts | null = c.listing_facts ?? null;
  const listingsN = (n: number) => fill(t("residence.listings_n"), { n });
  // The researched profile's facts lead; a catalogue row on the same
  // subject (deed, heating, ceiling, lifts) is then left out, and the
  // building's size rows go when the top already prints it.
  const profileFacts = (profile?.facts ?? []).filter((f) => {
    if (f.key === "builder" && devs.length === 0 && !c.developer) return false; // the top shows it
    if (f.key === "floors" && maxFloor) return false;
    if (f.key === "blocks" && buildingCount) return false;
    if (f.key === "flats" && flats) return false;
    return true;
  });
  const profileHas = new Set(profileFacts.map((f) => f.key));
  const aboutRows: Array<{ k: string; label: string; value: React.ReactNode; wide?: boolean }> = profileFacts.map((f) => ({
    k: `p.${f.key}`, label: f.label, value: f.value, wide: f.value.length > 40,
  }));
  if (c.deed_status && !profileHas.has("deed")) {
    aboutRows.push({ k: "deed", label: t("residence.about.deed"), value: localizeCatalogValue("deed_status", c.deed_status, locale) });
  }
  if (c.heating && !profileHas.has("heating")) {
    // When the listings back the catalogue's heating, say how many do.
    const agrees = lf?.heating && (lf.heating.value === "individual" ? "autonomous" : "central") === c.heating;
    aboutRows.push({
      k: "heating",
      label: t("residence.about.heating"),
      value: <>{localizeCatalogValue("heating", c.heating, locale)}{agrees && <span className="font-normal text-gray-500"> · {listingsN(lf!.heating!.listings)}</span>}</>,
    });
  }
  if (lf?.gas) {
    aboutRows.push({
      k: "gas",
      label: t("residence.about.gas"),
      value: <>{t(lf.gas.value ? "residence.gas.yes" : "residence.gas.no")}<span className="font-normal text-gray-500"> · {listingsN(lf.gas.listings)}</span></>,
    });
  }
  if (lf?.service_fee.length) {
    // The monthly fee as the sellers state it: an agreed figure with its
    // listing count, a lone seller's figure marked as one listing's.
    aboutRows.push({
      k: "fee",
      label: t("residence.about.fee"),
      wide: lf.service_fee.length > 1,
      value: (
        <span className="tabular-nums">
          {lf.service_fee.map((f, i) => (
            <span key={`${f.unit}-${f.amount_azn}`}>
              {i > 0 && ", "}
              <span className="whitespace-nowrap">{fill(t(f.unit === "per_m2_month" ? "residence.fee.per_m2_month" : "residence.fee.per_month"), { v: decimal(f.amount_azn, locale) })}</span>
              <span className="font-normal text-gray-500"> · {f.listings === 1 ? t("residence.fee.one") : listingsN(f.listings)}</span>
            </span>
          ))}
        </span>
      ),
    });
  }
  if (c.ceiling_height_m && !profileHas.has("ceiling")) {
    aboutRows.push({ k: "ceiling", label: t("residence.about.ceiling"), value: `${c.ceiling_height_m} m` });
  }
  // Lifts: what the residence's sellers name, with how many of them do.
  if (c.lift_brands?.length && !profileHas.has("lifts")) {
    aboutRows.push({
      k: "lifts",
      label: t("residence.about.lifts"),
      wide: true,
      value: c.lift_brands.map((l) => `${l.brand} (${fill(t("residence.about.lifts.by"), { n: l.sellers })})`).join(", "),
    });
  }
  const amenityList = lf?.amenities ?? [];

  // ── The top's facts (owner, 2026-11-01). Each one only when the
  // catalogue or the live listings carry it. "This year" is the server's
  // read day, so a page cached over New Year hydrates with what it said.
  const nowYear = Number(fresh.day.slice(0, 4)) || 2026;
  const state = buildState(c.construction_status, c.year_built, nowYear);
  const stateText = !state ? null
    : state.kind === "done"
      ? (state.year ? fill(t("residence.top.done_year"), { year: state.year }) : t("residence.top.done"))
      : state.kind === "building"
        ? (state.year ? fill(t("residence.top.building_year"), { year: state.year }) : t("residence.top.building"))
        : state.kind === "built"
          ? fill(t("residence.top.built_year"), { year: state.year })
          : fill(t("residence.top.handover_year"), { year: state.year });
  // The building in one line under the address (Google review 2026-10-13:
  // the pages that outrank us for a residence's name lead with floors,
  // buildings and flats).
  const buildingParts: string[] = [];
  if (maxFloor) buildingParts.push(fill(t("residence.about.insight.floors"), { v: maxFloor }));
  if (buildingCount) buildingParts.push(fill(t("residence.about.insight.buildings"), { v: buildingCount }));
  if (flats) buildingParts.push(fill(t("residence.about.insight.flats"), { v: flats }));
  const buildingLine = buildingParts.length
    ? buildingParts.join(" · ").replace(/^./, (ch) => ch.toLocaleUpperCase(locale))
    : null;
  const countText = (n: number) =>
    fill(t(`residence.top.count.${pluralKey(n, locale)}` as TranslationKey), { n });
  const jumpTo = (id: string, text: string) => (
    <a href={`#${id}`} onClick={(e) => glideToSection(e, id)} className={INLINE_LINK}>{text}</a>
  );
  const saleFrom = summary.live.sale.from_price ?? null;
  const rentFrom = summary.live.rent.from_price ?? null;
  const facts: Array<{ key: string; label: string; value: React.ReactNode; sub?: React.ReactNode }> = [];
  if (saleN > 0) {
    const fresh7 = newHere > 0 ? <span className="text-emerald-700">{fill(t("residence.hero.new7d"), { n: newHere })}</span> : null;
    facts.push({
      key: "sale",
      label: t("residence.top.sale"),
      value: saleFrom
        ? <span className="whitespace-nowrap">{fill(t("residence.top.from"), { price: money(saleFrom) })}</span>
        : jumpTo("satis", countText(saleN)),
      sub: saleFrom
        ? <>{jumpTo("satis", countText(saleN))}{fresh7 && <> · {fresh7}</>}</>
        : fresh7,
    });
  }
  if (rentN > 0) {
    facts.push({
      key: "rent",
      label: t("residence.top.rent"),
      value: rentFrom
        ? <span className="whitespace-nowrap">{fill(t("residence.top.rent_from"), { price: money(rentFrom) })}</span>
        : jumpTo("kiraye", countText(rentN)),
      sub: rentFrom ? jumpTo("kiraye", countText(rentN)) : undefined,
    });
  }
  if (devs.length === 0 && !c.developer && profile?.builder) {
    facts.push({ key: "developer", label: t("residence.about.developer"), value: profile.builder });
  } else if (devs.length > 0 || c.developer) {
    facts.push({
      key: "developer",
      label: t("residence.about.developer"),
      // Verified developers link to their page, one a line (two names
      // side by side ran off a 360 px phone); c.developer is their names
      // joined, as the backend sends it.
      value: devs.length > 0
        ? (
            <span className="flex flex-col gap-1">
              {devs.map((d) => (
                <span key={d.slug} className="flex min-w-0 items-start gap-1.5">
                  <DeveloperLogo name={d.name} logo={d.logo} size={20} />
                  <Link href={`/tikinti-sirketleri/${d.slug}`} className={`min-w-0 ${INLINE_LINK}`}>{d.name}</Link>
                </span>
              ))}
            </span>
          )
        : c.developer,
    });
  }
  if (stateText) facts.push({ key: "state", label: t("residence.top.status"), value: stateText });

  // Rail deals teaser: the 2 most-underpriced active cards (two full
  // cards keep the rail short; the AI card's button lists them all) -
  // concrete proof of the AI card's "N deals here" claim, one click from
  // the live detail drawer. Only clearly-below-model rows qualify.
  const railDeals = glanceCards
    .filter((l) => l.ai_overpriced_pct != null && l.ai_overpriced_pct <= -5)
    .sort((a, b) => (a.ai_overpriced_pct ?? 0) - (b.ai_overpriced_pct ?? 0))
    .slice(0, 2);

  // District intel tiles (inside the prices section).
  const intelTiles: Array<{
    key: string; label: string; value: string; unit?: string; sub?: React.ReactNode;
  }> = [];
  if (intel && summary.district.name) {
    if (intel.median_ppm != null) {
      intelTiles.push({
        key: "ppm",
        label: t("residence.intel.ppm"),
        value: fmt(intel.median_ppm),
        unit: "AZN/m²",
        sub: intel.ppm_momentum_pct != null ? (
          <div className={`mt-1 flex items-center gap-0.5 text-xs font-semibold tabular-nums ${
            intel.ppm_momentum_pct > 0.4
              ? "text-emerald-600"
              : intel.ppm_momentum_pct < -0.4 ? "text-rose-600" : "text-gray-500"
          }`}>
            {intel.ppm_momentum_pct > 0.4
              ? <ArrowUpRight className="w-3 h-3" />
              : intel.ppm_momentum_pct < -0.4
                ? <ArrowDownRight className="w-3 h-3" />
                : <Minus className="w-3 h-3" />}
            {Math.abs(intel.ppm_momentum_pct).toFixed(1)}% · {t("residence.hero.trend4w")}
          </div>
        ) : null,
      });
    }
    if (intel.stock_now > 0) {
      intelTiles.push({ key: "stock", label: t("residence.intel.stock"), value: fmt(intel.stock_now) });
    }
    if (intel.new_7d > 0) {
      intelTiles.push({ key: "new7d", label: t("residence.intel.new7d"), value: `+${fmt(intel.new_7d)}` });
    }
  }
  const showIntel = Boolean(intel && summary.district.name && (intelTiles.length > 0 || premium != null));
  const showSaleTrend = !quiet && summary.trend_sale.length > 0;
  const hasPrices = showIntel || segKeys.length > 0 || Boolean(summary.renovation) || showRepairImpact || showSaleTrend;
  const hasAbout = aboutRows.length > 0 || Boolean(about) || Boolean(profile?.summary) || amenityList.length > 0;

  // FAQ links: where the answer's live data is on this page, else the
  // listings page that holds it.
  const faqLink = (kind: string | null | undefined): React.ReactNode => {
    const target = kind === "sale"
      ? (saleN > 0 ? "#satis" : browseHref)
      : kind === "rent"
        ? (rentN > 0 ? "#kiraye" : rentHref)
        : kind === "photos"
          ? (galleryPhotos.length >= 2 ? "#foto" : null)
          : kind === "plans"
            ? (planList.length > 0 ? "#planlar" : null)
            : kind === "prices"
              ? (hasPrices ? "#qiymetler" : saleN > 0 ? "#satis" : browseHref)
              : null;
    if (!target || !kind) return null;
    const label = t(`residence.faq.link.${kind}` as TranslationKey);
    const cls = `inline-flex items-center gap-1 text-sm ${LINK}`;
    return target.startsWith("#")
      ? <a href={target} onClick={(e) => glideToSection(e, target.slice(1))} className={cls}>{label}<ChevronRight className={`h-4 w-4 ${CHEVRON}`} aria-hidden /></a>
      : <Link href={target} className={cls}>{label}<ChevronRight className={`h-4 w-4 ${CHEVRON}`} aria-hidden /></Link>;
  };
  const faqEntries = faq.map((f) => ({ q: f.q, a: f.a, extra: faqLink(f.link) }));

  // The AI card, rendered ONCE (it used to be in the page twice, a phone
  // copy and a rail copy): under the top on phones, heading the side
  // column on a computer (the grid below places it).
  // The quiet UI (2026-10-29): a white card with the hairline border and
  // the one primary action solid indigo. Its follow button is gone (owner,
  // 2026-11-02): the top's follow block says it once. A quiet residence
  // has no listings to browse, so it gets no card at all.
  const ctaCard = quiet ? null : (
    <section className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="min-w-0">
        <h2 className="text-lg font-bold leading-snug text-gray-900">
          {dealsN > 0
            ? (dealsN === 1
                ? t("residence.cta.deals.title.one")
                : fill(t("residence.cta.deals.title"), { n: dealsN }))
            : t("residence.cta.nodeals.title")}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {dealsN > 0 ? t("residence.cta.deals.body") : t("residence.cta.nodeals.body")}
        </p>
        <Link
          href={dealsN > 0 ? dealsHref : browseHref}
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 transition-colors"
        >
          {dealsN > 0 ? t("residence.cta.deals.button") : t("residence.cta.nodeals.button")}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );

  const seeAllClass = `mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold ${BUTTON_SECONDARY} transition-colors`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      {/* max-w-3xl column on mobile/tablet; widens to a two-column
          grid (sections | side column) on lg+. */}
      <main className="max-w-3xl lg:max-w-6xl mx-auto px-4 pt-6 pb-16">
        {/* Breadcrumb: Home › Residences › this residence. (Mirrored
            by the BreadcrumbList JSON-LD in page.tsx string-for-string.) */}
        <nav aria-label={t("common.breadcrumb")} className="mb-5 text-sm text-gray-600 enter-up">
          <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <li>
              <Link href="/" className="hover:text-gray-900 transition-colors">
                {t("breadcrumb.home")}
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-400">›</li>
            <li>
              <Link href="/residences" className="hover:text-gray-900 transition-colors">
                {/* Short crumb on phones: the hub's full SEO title
                    ("...residential complexes - prices and comparison")
                    wrapped the whole breadcrumb row off-screen. */}
                <span className="sm:hidden">{t("nav.residences")}</span>
                <span className="hidden sm:inline">{t("residences.page.title")}</span>
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-400">›</li>
            <li>
              <span aria-current="page" className="text-gray-900 font-medium">
                {summary.name}
              </span>
            </li>
          </ol>
        </nav>

        {/* The top (owner, 2026-11-01, after the korter.az review): the
            photo beside the facts. Phones stack the photo over them; from
            sm up the photo takes the left column at the facts' height.
            Plain <img> - remote admin-pasted URLs would need a next/image
            remote-pattern allowlist per host. */}
        <div className="mt-4 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          {/* The photo rises without the fade (enter-rise): Chrome never
              counts anything painted at opacity 0 as the Largest
              Contentful Paint, so with enter-up the phone LCP fell to the
              follow card's sentence, repainted once the session check
              answered (mobile Lighthouse 6.0 s, 2026-10-20). */}
          <div className="relative enter-rise" style={{ animationDelay: "0.12s" }}>
            {heroSrc ? (
              <HeroFrame
                onOpen={viewerPhotos.length > 0 ? () => setViewerAt(0) : null}
                label={fill(t("residence.hero.open_gallery"), { name })}
              >
              <Photo
                src={heroSrc}
                // A hero file that fails (moved, deleted) shows the first
                // outside shot rather than an empty frame.
                fallbackSrc={summary.image_url ? galleryFirst : null}
                fallback={
                  <span className="absolute inset-0 bg-[#FAF9F6] dark:bg-[#1E2434]">
                    <Illo base={placeholderFor(summary.cluster_id)} className="absolute inset-0 h-full w-full object-contain" />
                  </span>
                }
                alt={heroAlt ?? summary.name}
                frame="w-full h-44 sm:h-full sm:min-h-72 rounded-2xl border border-gray-200"
                // The hero photo IS the largest element above the fold on
                // the pages Google sends most visitors to; lazy-loading it
                // pushed LCP behind the scripts (replay analysis 2026-09-15).
                loading="eager"
                fetchPriority="high"
                // Phones take a 768 or 1024 px copy of the 1600 px file
                // (Belvedere's is 106 KB); the media route resizes and the
                // edge caches each width.
                sources={summary.image_url ? (
                  <source
                    media="(max-width: 640px)"
                    sizes="100vw"
                    srcSet={`${mediaThumb(summary.image_url, 768)} 768w, ${mediaThumb(summary.image_url, 1024)} 1024w`}
                  />
                ) : undefined}
              />
              </HeroFrame>
            ) : (
              // The frame is wider than the 3:2 scene, so the scene sits whole
              // on its own paper tone rather than losing its roof to a crop.
              <div className="relative w-full h-44 sm:h-full sm:min-h-72 overflow-hidden rounded-2xl border border-gray-200 bg-[#FAF9F6] dark:bg-[#1E2434]">
                <Illo base={placeholderFor(summary.cluster_id)} className="absolute inset-0 h-full w-full object-contain" />
              </div>
            )}
            <OfficialLinks
              links={summary.official_links}
              variant="circles"
              className="absolute bottom-2.5 right-2.5"
            />
          </div>

          {/* The facts. Plain <div>, NOT a semantic <header> (globals.css
              styles `header` for the top nav). */}
          <div className="min-w-0 enter-rise" style={{ animationDelay: "0.06s" }}>
            <h1 className="text-3xl font-extrabold leading-[1.1] tracking-tight text-gray-900">
              {name}
              {h1Tail && (
                <>
                  <span className="sr-only">: </span>
                  <span className="mt-1 block text-base font-semibold tracking-normal text-gray-600">{h1Tail}</span>
                </>
              )}
            </h1>
            {/* Popular alternative names, visible so Google can rank the
                page for alias queries (the JSON-LD alternateName carries
                the same list). */}
            {(akaNames ?? summary.display_aliases ?? []).length > 0 && (
              <p className="mt-1 text-sm text-gray-500">
                {t("residence.aka")}{" "}
                {(akaNames ?? summary.display_aliases ?? []).join(", ")}
              </p>
            )}
            {c.address_full && mapsHref ? (
              // items-start (not center) so the pin aligns to the first
              // line when the address wraps to two; the address links out
              // to Google Maps (new tab), the area follows it.
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-start gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
              >
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                <span className="group-hover:underline underline-offset-2">
                  {[c.address_full, summary.district.name].filter(Boolean).join(", ")}
                </span>
              </a>
            ) : summary.district.name ? (
              <p className="mt-2 inline-flex items-start gap-1 text-sm text-gray-600">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                <span>{summary.district.name}</span>
              </p>
            ) : null}
            {buildingLine && (
              <p className="mt-1 flex items-start gap-1 text-sm tabular-nums text-gray-600">
                <Building2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" aria-hidden />
                <span>{buildingLine}</span>
              </p>
            )}

            {/* The googled-for number, big, immediately. A residence the
                weekly comparables skipped shows the same number from its
                live priced listings when at least three carry an AZN/m²,
                labelled as such (ticket 34). Fewer, or none: no number,
                no dash and no note; the top keeps its shape and the
                statistic is simply absent (owner, 2026-10-18). */}
            {overall?.m ? (
              <div className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-2">
                <div>
                  <div className="text-4xl font-extrabold text-gray-900 tabular-nums leading-none">
                    {fmt(overall.m)}
                    <span className="ml-1.5 text-lg font-semibold text-gray-500">AZN/m²</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {t("residence.stat.ppm")} · {fill(t("residence.stat.basis"), { n: overall.n ?? 0 })}
                  </div>
                </div>
                {overall.p ? (
                  <div>
                    <div className="text-2xl font-bold text-gray-800 tabular-nums leading-none">
                      ~{fmt(overall.p)}
                      <span className="ml-1 text-sm font-semibold text-gray-500">AZN</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {t("residence.stat.price")}{overall.a ? ` · ~${overall.a} m²` : ""}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : saleN >= 3 && summary.live.sale.median_ppm != null ? (
              <div className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-2">
                <div>
                  <div className="text-4xl font-extrabold text-gray-900 tabular-nums leading-none">
                    {fmt(summary.live.sale.median_ppm)}
                    <span className="ml-1.5 text-lg font-semibold text-gray-500">AZN/m²</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {t("residence.stat.ppm")} · {fill(t("residence.price.live_basis"), { n: saleN })}
                  </div>
                </div>
                {summary.live.sale.median_price ? (
                  <div>
                    <div className="text-2xl font-bold text-gray-800 tabular-nums leading-none">
                      ~{fmt(summary.live.sale.median_price)}
                      <span className="ml-1 text-sm font-semibold text-gray-500">AZN</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {t("residence.stat.price")}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* The facts as a spec sheet between two hairlines: "from"
                prices with their listing counts (the counts jump to the
                sale and rent sections), the developer, built or under
                construction. */}
            {facts.length > 0 && (
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-y border-gray-200 py-3 text-sm">
                {facts.map((f, i) => (
                  <div key={f.key} className={`min-w-0 ${facts.length % 2 === 1 && i === facts.length - 1 ? "col-span-2" : ""}`}>
                    <dt className="text-xs text-gray-500">{f.label}</dt>
                    <dd className="mt-0.5 font-semibold text-gray-900 tabular-nums">{f.value}</dd>
                    {f.sub && <dd className="mt-0.5 text-xs text-gray-600 tabular-nums">{f.sub}</dd>}
                  </div>
                ))}
              </dl>
            )}
            {/* The day the page read the live listings. A quiet residence
                shows weekly statistics only, which "updated today" would
                overstate, so it goes without. */}
            {!quiet && <FreshLine day={fresh.day} label={fresh.label} className="mt-3" />}

            {/* The capture hook - one tap, push, no account needed (see
                FollowResidenceButton). Last in the top so the facts keep
                their prime spot. */}
            <div className="mt-4">
              <FollowResidenceButton
                clusterId={summary.cluster_id}
                hook={followHook}
                fallbackHref={alertHref}
                preview={summary.follow_preview}
              />
            </div>
          </div>
        </div>

        {/* Advisories - editorial incidents/notices, trust-critical. */}
        <AdvisoryBanner advisories={summary.advisories} className="mt-5" />

        {/* lg+: the sections read down the wide left column; the AI card
            heads the right one, and the reference blocks under it stick
            (the grid's second row runs the main column's length). Below
            lg it is one column in source order: the AI card right under
            the top, then the sections, then the reference blocks. */}
        <div className="enter-up lg:grid lg:grid-cols-[minmax(0,1fr)_21rem] lg:grid-rows-[auto_1fr_auto] lg:gap-x-10" style={{ animationDelay: "0.2s" }}>
        {ctaCard && <div className="mt-6 lg:col-start-2 lg:row-start-1 lg:mt-10">{ctaCard}</div>}
        <div className="min-w-0 lg:col-start-1 lg:row-start-1 lg:row-span-2">

        {/* What was here before: a quiet residence shows the listings it
            had (newest departure first) where the live ones would sit,
            so "no listings" never reads as "never any" (ticket 34). */}
        {quiet && (previous?.results?.length ?? 0) > 0 && (
          <PreviousListings cards={previous!.results} lastSeenAt={summary.previous?.last_seen_at} />
        )}

        {/* ABOUT first (owner, 2026-11-01: everything about this residence
            before the comparisons): it used to be "Rezidensiya haqqında" in the
            main column and "Kompleks haqqında" in the side one): every
            catalogue fact, then the residence in words. */}
        {hasAbout && (
          <section id="haqqinda" aria-labelledby="haqqinda-h" className={SECTION}>
            <h2 id="haqqinda-h" className={H2}>{fill(t("residence.section.about"), { name })}</h2>
            <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              {profile?.summary && (
                <p className="mb-5 text-sm leading-relaxed text-gray-700 sm:text-base">{profile.summary}</p>
              )}
              {aboutRows.length > 0 && (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
                  {aboutRows.map((r) => (
                    <div key={r.k} className={r.wide ? "col-span-2 sm:col-span-3" : ""}>
                      <dt className="text-xs text-gray-500">{r.label}</dt>
                      <dd className="mt-0.5 font-medium text-gray-900">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {/* What the residence's own listings say, each kind with how
                  many listings state it (two or more sellers at least). */}
              {amenityList.length > 0 && (
                <div className={aboutRows.length > 0 || profile?.summary ? "mt-5 border-t border-gray-100 pt-5" : ""}>
                  <h3 className={H3}>{t("residence.listings_say.title")}</h3>
                  <ul className="flex flex-wrap gap-1.5">
                    {amenityList.map((a) => {
                      const Icon = AMENITY_ICONS[a.key];
                      const label = t(`residence.amen.${a.key}` as TranslationKey);
                      const body = (
                        <>
                          {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />}
                          {label}
                          {a.listings > 0 && <span className="tabular-nums text-xs font-normal text-gray-500">{a.listings}</span>}
                        </>
                      );
                      // A chip opens exactly the listings that state it (still
                      // on sale or for rent here); with none left it is a
                      // plain label without a count.
                      return (
                        <li key={a.key}>
                          {a.listings > 0 ? (
                            <Link
                              href={`/listings?complex_slug=${encodeURIComponent(summary.cluster_id)}&amenity=${a.key}&leased=all`}
                              aria-label={`${label}: ${listingsN(a.listings)}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            >
                              {body}
                            </Link>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700">{body}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="mt-2 text-xs text-gray-500">{t("residence.amen.note")}</p>
                </div>
              )}
              {(profile?.timeline.length ?? 0) > 0 && (
                <div className="mt-5 border-t border-gray-100 pt-5">
                  <h3 className={H3}>{t("residence.profile.timeline")}</h3>
                  <ol className="space-y-2 text-sm">
                    {profile!.timeline.map((s) => (
                      <li key={`${s.when}-${s.text.slice(0, 12)}`} className="grid grid-cols-[4.5rem_1fr] gap-x-3">
                        <span className="font-semibold tabular-nums text-gray-900">{s.when}</span>
                        <span className="text-gray-700">{s.text}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {about && (
                <div className={aboutRows.length > 0 || amenityList.length > 0 ? "mt-5 border-t border-gray-100 pt-5" : ""}>{about}</div>
              )}
              {/* The rest of the researched profile, behind the toggle: the
                  longer notes and the sources (server-rendered, so crawlers
                  keep the text). */}
              {profile && (profile.more.length > 0 || profile.sources.length > 0) && (
                <SectionExpander className="mt-5 border-t border-gray-100 pt-4" show={t("residence.profile.more")} hide={t("residence.profile.less")}>
                  <div className="space-y-4 text-sm leading-relaxed text-gray-700">
                    {profile.more.map((m, i) => (
                      <div key={i}>
                        {m.title && <h3 className="mb-1 text-sm font-semibold text-gray-900">{m.title}</h3>}
                        {m.body.split(/\n\s*\n/).map((para, j) => <p key={j} className={j ? "mt-2" : ""}>{para}</p>)}
                      </div>
                    ))}
                    {profile.sources.length > 0 && (
                      <div>
                        <h3 className="mb-1 text-sm font-semibold text-gray-900">{t("residence.profile.sources")}</h3>
                        <ul className="space-y-1 text-xs">
                          {profile.sources.map((src) => (
                            <li key={src.url} className="min-w-0 truncate">
                              <a href={src.url} target="_blank" rel="noopener noreferrer nofollow" className={INLINE_LINK}>
                                {src.title || src.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </SectionExpander>
              )}
            </div>
          </section>
        )}

        {/* SALE: the heading people search ("... satılan mənzillər"), the
            count and the ortalama, prices by rooms, then the apartments
            (server-fetched, opening the live detail drawer, with a "see
            all" link into the pre-filtered /listings funnel). Residence
            pages are the #1 entry surface: someone googling a building
            name wants to SEE apartments. */}
        {saleN > 0 && (
          <section id="satis" aria-labelledby="satis-h" className={SECTION}>
            <h2 id="satis-h" className={H2}>{fill(t("residence.section.sale"), { name })}</h2>
            <SummaryLine
              parts={[
                countText(saleN),
                summary.live.sale.median_price ? fill(t("residence.summary.avg"), { price: money(summary.live.sale.median_price) }) : null,
              ]}
              chip={dealsN > 0 ? (
                // Deals = the product hook: the deal colour (emerald, as
                // the cards' deal chips), not the brand (the quiet UI).
                <Link href={dealsHref} className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100">
                  {fill(t("residence.live.deals_chip"), { n: dealsN })}
                </Link>
              ) : null}
            />
            {roomsSale}
            {glanceCards.length > 0 && (
              <div className="mt-6">
                <h3 className={H3}>{t("residence.sale.cards")}</h3>
                {/* Rooms + sort - re-queries the same complex-scoped
                    /listings; the defaults render the server-fetched cards
                    untouched. Numeric room chips are the bina-era pattern
                    every Baku searcher already reads fluently. */}
                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  {([null, 1, 2, 3, 4] as const).map((r) => (
                    <button
                      key={r === null ? "all" : r}
                      type="button"
                      onClick={() => setRoomFilter(r)}
                      aria-pressed={roomFilter === r}
                      className={
                        roomFilter === r
                          ? `rounded-lg border px-2.5 py-1 text-xs font-semibold ${SELECTED}`
                          : "rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-colors"
                      }
                    >
                      {r === null
                        ? t("residence.filters.all")
                        : r === 4
                          ? "4+"
                          : String(r)}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={cycleSort}
                    className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-colors"
                  >
                    <ArrowUpDown className="w-3 h-3" />
                    {glanceSort === "newest"
                      ? t("residence.filters.sort.newest")
                      : glanceSort === "price"
                        ? t("residence.filters.sort.cheap")
                        : t("residence.filters.sort.expensive")}
                  </button>
                </div>
                {displayCards.length === 0 ? (
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center">
                    <p className="text-sm text-gray-600">{t("residence.filters.empty")}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setRoomFilter(null);
                        setGlanceSort("newest");
                      }}
                      className="mt-2 text-sm font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                    >
                      {t("residence.filters.reset")}
                    </button>
                  </div>
                ) : (
                  <div
                    className={`grid grid-cols-2 sm:grid-cols-3 auto-rows-fr gap-3 transition-opacity ${
                      filterBusy ? "opacity-60" : ""
                    }`}
                  >
                    {displayCards.map((listing, i) => (
                      <div
                        key={listing.id}
                        // Phones get 2x2; the 3rd row only exists from sm up -
                        // part of the mobile length diet (the "see all N" CTA
                        // right below carries the tail).
                        className={i >= 4 ? "hidden sm:block sm:h-full" : "h-full"}
                      >
                        {/* A real /elan link under the click handler, as on /listings:
                            crawlers saw a residence with "203 flats for sale" and no
                            listing on it. */}
                        <ListingBrowseCard
                          listing={listing}
                          onOpen={openDetail}
                          href={(() => { const lp = listingPath(listing.id); return lp ? `/${locale}${lp}` : undefined; })()}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <Link
                  href={browseHref}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 transition-colors"
                >
                  {fill(t("residence.listings.see_all"), { n: displayTotal })}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </section>
        )}

        {/* PLANS: every floor plan the residence's listings showed, one per
            layout, kept after a listing leaves its site (owner, 2026-11-01;
            korter.az has a layouts page, ours is live), filterable to the
            ones for sale now. */}
        {planList.length > 0 && (
          <section id="planlar" aria-labelledby="planlar-h" className={SECTION_LATER}>
            <h2 id="planlar-h" className={H2}>{fill(t("residence.section.plans"), { name })}</h2>
            <p className="mt-1 text-xs text-gray-500">{t("residence.plans.note")}</p>
            <ResidencePlans tiles={planList} name={name} locale={locale} onOpen={openDetail} />
            {plansTotal > planList.filter((p) => p.card).length && (
              <Link
                href={`${browseHref}&photo_has=floor_plan`}
                className={`mt-3 inline-flex items-center gap-1 text-sm ${LINK}`}
              >
                {fill(t("residence.plans.all"), { n: plansTotal })}
                <ChevronRight className={`h-4 w-4 ${CHEVRON}`} aria-hidden />
              </Link>
            )}
          </section>
        )}

        {/* RENT: its own section now (it was a small link beside the sale
            cards), with the rents by rooms, the cards and the rent trend. */}
        {rentN > 0 && (
          <section id="kiraye" aria-labelledby="kiraye-h" className={SECTION_LATER}>
            <h2 id="kiraye-h" className={H2}>{fill(t("residence.section.rent"), { name })}</h2>
            <SummaryLine
              parts={[
                countText(rentN),
                summary.live.rent.median_price
                  ? fill(t("residence.summary.avg"), { price: `${money(summary.live.rent.median_price)}${t("residence.live.month")}` })
                  : null,
              ]}
            />
            {roomsRent}
            {rentCards.length > 0 && (
              <div className="mt-6">
                <h3 className={H3}>{t("residence.sale.cards")}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 auto-rows-fr gap-3">
                  {rentCards.map((listing, i) => (
                    <div key={listing.id} className={i >= 4 ? "hidden sm:block sm:h-full" : "h-full"}>
                      <ListingBrowseCard
                        listing={listing}
                        onOpen={openDetail}
                        href={(() => { const lp = listingPath(listing.id); return lp ? `/${locale}${lp}` : undefined; })()}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* White, not indigo: the sale "see all" is the page's one
                primary action. */}
            <Link href={rentHref} className={seeAllClass}>
              {fill(t("residence.rent.see_all"), { n: rentTotal })}
              <ChevronRight className={`h-4 w-4 ${CHEVRON}`} aria-hidden />
            </Link>
            {!quiet && summary.trend_rent.length > 0 && (
              <div className="mt-6">
                <h3 className={H3}>{t("residence.trend.rent.title")}</h3>
                <WhenVisible className="h-48 rounded-xl bg-gray-50">
                  <TrendChart
                    level="complex"
                    locationKey={summary.cluster_id}
                    mode="rent"
                    rooms={0}
                    weeks={52}
                  />
                </WhenVisible>
              </div>
            )}
          </section>
        )}

        {/* PRICES: what flats here cost and why - the district around it,
            the asking-price segments, renovation and the sale trend. */}
        {hasPrices && (
          <section id="qiymetler" aria-labelledby="qiymetler-h" className={SECTION_LATER}>
            <h2 id="qiymetler-h" className={H2}>{fill(t("residence.section.prices"), { name })}</h2>

            {/* District intel - market context around the complex, plus the
                headline complex-vs-district premium, in ONE card (a
                prominent premium band + a tight divided stat row): the
                "how this residence compares to its district" insight - the
                reason a buyer is on this page - leads. */}
            {showIntel && (
              <div className="mt-4">
                <h3 className={H3}>
                  {fill(t("residence.intel.title"), { district: summary.district.name ?? "" })}
                </h3>
                <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                  {premium != null && (
                    // Dark-mode colours are explicit hexes, not Tailwind
                    // amber-/emerald-* utilities: this page's dark theme
                    // remaps the named palette (light shades go DARK), which
                    // turned the band text near-invisible. Hexes bypass the
                    // remap (same trick the AI card uses).
                    <div className={`flex items-center gap-2 px-4 py-3 text-[15px] font-bold border-b border-gray-100 dark:border-white/10 ${
                      premium > 0
                        ? "bg-amber-50 text-amber-800 dark:bg-[#3f2d10] dark:text-[#fcd34d]"
                        : "bg-emerald-50 text-emerald-800 dark:bg-[#0d2f22] dark:text-[#34d399]"
                    }`}>
                      {premium > 0
                        ? <ArrowUpRight className="w-4 h-4 shrink-0" />
                        : <ArrowDownRight className="w-4 h-4 shrink-0" />}
                      {fill(
                        premium > 0 ? t("residence.premium.above") : t("residence.premium.below"),
                        { pct: Math.abs(premium).toFixed(0) },
                      )}
                    </div>
                  )}
                  {/* The district-market tiles stay ALWAYS visible - the
                      owner explicitly wants this block un-collapsed (the
                      premium band leads, the tiles support it). */}
                  {intelTiles.length > 0 && (
                    <div className={`grid grid-cols-1 ${intelTiles.length >= 3 ? "sm:grid-cols-3" : intelTiles.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"} divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-white/10`}>
                      {intelTiles.map((tile) => (
                        <div key={tile.key} className="px-4 py-4">
                          <div className="text-xs text-gray-500">
                            {tile.label}
                          </div>
                          {/* Number leads - big + extrabold so the eye lands
                              on the value, not the label. Unit demoted. */}
                          <div className="mt-1.5 flex items-baseline gap-1">
                            <span className="text-2xl sm:text-3xl font-extrabold text-gray-900 tabular-nums leading-none">
                              {tile.value}
                            </span>
                            {tile.unit && <span className="text-xs text-gray-500">{tile.unit}</span>}
                          </div>
                          {tile.sub}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Asking-price segments - one computed conclusion leads (the
                priciest vs the most affordable segment); the per-segment
                table stays open: the owner reads it as the block's
                substance, not a detail (2026-09-20). */}
            {segKeys.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-1 text-sm font-semibold text-gray-900">
                  {t("residence.segments.title")}
                </h3>
                {segInsight && <InsightChips text={segInsight} />}
                <p className="mt-2 text-xs text-gray-500 mb-3">{t("residence.segments.note")}</p>
                <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
                  {/* Fixed columns (label | ~price | AZN/m² | count) so the
                      AZN/m² figures align vertically across every row. */}
                  {segKeys.map((k) => {
                    const s = segments[k];
                    return (
                      <div
                        key={k}
                        className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_7.5rem_7.5rem_5rem] items-center gap-x-2 px-4 py-2.5 text-sm"
                      >
                        <span className="text-gray-700">
                          {translate(`residence.seg.${k}` as TranslationKey, locale)}
                        </span>
                        <span className="hidden sm:block text-right tabular-nums text-gray-500">
                          {s.p != null ? `~${fmt(s.p)} AZN` : ""}
                        </span>
                        <span className="text-right tabular-nums font-semibold text-gray-900">
                          {fmt(s.m)} AZN/m²
                        </span>
                        <span className="hidden sm:block text-right text-xs text-gray-500 tabular-nums">
                          {fill(translate("district.complexes.listings", locale), { n: s.n })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Renovation profile - the vision model's condition tiers over
                the building's ACTIVE listing galleries as an INTERACTIVE
                DONUT (content nobody else in the market has): paints itself
                on scroll-in, hover/tap a tier to highlight it everywhere.
                Self-hides below 3 scored galleries. */}
            {summary.renovation && (() => {
              const reno = summary.renovation;
              const RENO_TIERS: [string, string][] = [
                ["luxury", "#a78bfa"],
                ["modern", "#34d399"],
                ["mid", "#38bdf8"],
                ["dated", "#fbbf24"],
                ["shell", "#fb7185"],
              ];
              const items = RENO_TIERS.filter(([tier]) => (reno.dist[tier] ?? 0) > 0).map(
                ([tier, color]) => {
                  const n = reno.dist[tier] ?? 0;
                  const pct = (n / reno.n) * 100;
                  const rounded = Math.round(pct);
                  return {
                    tier, color, n, pct,
                    pctLabel: rounded === 0 ? "<1%" : `${rounded}%`,
                    label: t(`photointel.tier.${tier}` as TranslationKey),
                    shortLabel: t(`photointel.tier_short.${tier}` as TranslationKey),
                  };
                },
              );
              return (
                <div className="mt-6">
                  <h3 className={H3}>{t("residence.renovation.title")}</h3>
                  <RenovationDonut
                    items={items}
                    note={t("residence.renovation.note").replace("{n}", String(reno.n))}
                    hrefFor={(tier) => `${browseHref}&photo_condition=${tier}`}
                    hint={t("residence.renovation.hint")}
                  />
                </div>
              );
            })()}

            {/* Renovation impact - renovated vs non-renovated AZN/m² per room,
                with the renovated-over-non-renovated difference. Only the room
                buckets where both cohorts cleared the sample gate appear. */}
            {showRepairImpact && (
              <div className="mt-6">
                <h3 className="mb-1 text-sm font-semibold text-gray-900">
                  {t("residence.repair.title")}
                </h3>
                {repairInsight && <InsightChips text={repairInsight} />}
                <p className="mt-2 text-xs text-gray-500 mb-3">{t("residence.repair.note")}</p>
                <SectionExpander show={t("residence.details.show")} hide={t("residence.details.hide")}>
                <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 dark:divide-white/10 overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto_auto_3.5rem] sm:grid-cols-[1fr_7.5rem_7.5rem_5rem] items-center gap-x-2 px-4 py-2 text-xs text-gray-500">
                    <span />
                    <span className="text-right">{t("residence.seg.with_repair")}</span>
                    <span className="text-right">{t("residence.seg.no_repair")}</span>
                    <span className="text-right">{t("residence.repair.diff")}</span>
                  </div>
                  {repairRows.map(({ rb, wr, nr }) => {
                    const diff = Math.round((wr.m / nr.m - 1) * 100);
                    return (
                      <div
                        key={rb}
                        className="grid grid-cols-[1fr_auto_auto_3.5rem] sm:grid-cols-[1fr_7.5rem_7.5rem_5rem] items-center gap-x-2 px-4 py-2.5 text-sm"
                      >
                        <span className="text-gray-700">
                          {translate(`residence.seg.${rb}` as TranslationKey, locale)}
                        </span>
                        <span className="text-right tabular-nums font-semibold text-gray-900">
                          {fmt(wr.m)} AZN/m²
                        </span>
                        <span className="text-right tabular-nums text-gray-600">
                          {fmt(nr.m)} AZN/m²
                        </span>
                        <span className="text-right tabular-nums font-semibold text-gray-700">
                          {signedPct(diff, 1, trimDecimals)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                </SectionExpander>
              </div>
            )}

            {/* Weekly sale trend - complex-level aggregates (fills in as
                weeks accrue). The rent trend sits in the rent section. */}
            {showSaleTrend && (
              <div className="mt-6">
                <h3 className={H3}>{translate("district.section.trend.sale", locale)}</h3>
                <WhenVisible className="h-48 rounded-xl bg-gray-50">
                  <TrendChart
                    level="complex"
                    locationKey={summary.cluster_id}
                    mode="sale"
                    rooms={0}
                    weeks={52}
                  />
                </WhenVisible>
              </div>
            )}
          </section>
        )}

        {/* Interest (Pro): how many people look, save and follow this
            residence, per listing on offer, last 4 weeks. Nothing on
            offer: nothing to count. */}
        {!quiet && <InterestCard kind="residence" slug={summary.cluster_id} className="mt-10" />}

        {/* Outside shots from the listings (scripts/build_residence_galleries.py). */}
        {/* Without an admin hero the first shot IS the top's photo; don't repeat it.
            After the prices, before the questions (the top photo opens
            the same viewer). */}
        <ResidenceGallery photos={galleryPhotos} name={name} className={SECTION_LATER} onOpen={(i) => setViewerAt(i + heroInViewer.length)} />
        {viewerAt != null && createPortal(
          <PhotoLightbox
            photos={viewerPhotos.map((p) => ({ url: fullPhoto(p.url), label: p.caption ?? null }))}
            initialIndex={viewerAt}
            onClose={() => setViewerAt(null)}
          />,
          document.body,
        )}

        {/* FAQ - the exact strings the FAQPage JSON-LD carries. */}
        {faq.length > 0 && (
          <section id="suallar" aria-labelledby="suallar-h" className={SECTION_LATER}>
            <h2 id="suallar-h" className={`${H2} mb-3`}>
              {fill(t("residence.faq.title_named"), { name })}
            </h2>
            <FaqList entries={faqEntries} />
          </section>
        )}

        {/* Community comments - moderated; only admin-approved text
            renders here. Client island so approvals show without
            waiting out the page's ISR window. */}
        <ResidenceComments clusterId={summary.cluster_id} locale={locale} />

        </div>

        {/* The reference blocks: under the AI card on a computer, where
            they stick while the sections scroll; after the sections on a
            phone. */}
        <aside ref={rail.ref} style={{ top: rail.top }} className="mt-10 space-y-6 lg:col-start-2 lg:row-start-2 lg:mt-6 lg:self-start lg:sticky">
          {location}

          {/* Concrete proof for the AI card's claim: the best-priced live
              listings as the page's own cards (owner, 2026-11-01: a lone
              row read as an afterthought), one tap from the detail drawer. */}
          {railDeals.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                {t("residence.rail.deals.title")}
              </h2>
              <div className="space-y-3">
                {railDeals.map((l) => (
                  <ListingBrowseCard
                    key={l.id}
                    listing={l}
                    onOpen={openDetail}
                    href={(() => { const lp = listingPath(l.id); return lp ? `/${locale}${lp}` : undefined; })()}
                  />
                ))}
              </div>
            </section>
          )}

        </aside>

        {/* Then the comparisons and discovery (owner, 2026-11-01): the deals
            around it, the district's other residences, the searches around
            it. Under the article column on a computer, last on a phone. */}
        <div className="min-w-0 lg:col-start-1 lg:row-start-3">
          {/* The deals of the residences around this one (feedback ticket
              65), first of the comparisons: for sale like the cards above,
              in the rooms their chips pick, six cards (four on phones) as
              the glance shows. A quiet residence gets them too, here. */}
          <NearbyDeals
            query={{
              clusterId: summary.cluster_id,
              leased: false,
              // "4+" is four and more: the listings page reads "5" as 5+.
              rooms: roomFilter === null ? [] : roomFilter === 4 ? ["4", "5"] : [String(roomFilter)],
              category: null,
            }}
            onOpen={openDetail}
            activeId={activeListing?.listingId ?? null}
            gridClassName="grid-cols-2 sm:grid-cols-3 auto-rows-fr"
            max={6}
            phoneMax={4}
            surface="residence"
            className="mt-8"
          />

          {/* Same-district residences - the internal-link mesh: every
              residence page passes link equity to its district siblings
              (complex-name queries ranked 7-10 with these pages link-
              starved; server-rendered so crawlers see the links). */}
          {(summary.nearby_residences?.length ?? 0) > 0 && (
            <section className="mt-10">
              <h2 className={`${H2} mb-3`}>
                {t("residence.nearby.title").replace("{district}", summary.district?.name ?? "")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {summary.nearby_residences!.map((r) => (
                  <Link
                    key={r.cluster_id}
                    href={`/residence/${r.cluster_id}`}
                    className="rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 hover:border-gray-300 hover:text-gray-900 transition-colors"
                  >
                    {r.name}
                  </Link>
                ))}
                <Link
                  href="/residences"
                  className="rounded-full border border-gray-200 bg-gray-100 px-3.5 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-200 transition-colors"
                >
                  {t("residence.nearby.all")}
                </Link>
              </div>
            </section>
          )}
          {/* The server-rendered tail (the searches made around it, the
              place's own searches) reads on in the article column; it used
              to land after <main>, edge to edge across the screen (owner,
              2026-10-18). */}
          {children ? <div className="mt-10 space-y-8">{children}</div> : null}
        </div>
        </div>

        {/* The compare-with-another entry closes the page. Its old twin
            of the AI card's button is gone: the card says it once. */}
        <div className="mt-10 flex justify-center text-center">
          <Link
            href={`/compare/residences?ids=${summary.cluster_id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
          >
            {t("rescompare.cta")}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

      </main>

      {/* Live listing detail drawer (portal overlay, same as /map).
          Mounted only after a card is clicked, so the lazy chunk never
          loads for the 95% who just read the page. */}
      {activeListing && (
        <ListingDetailPanel
          externalId={activeListing.externalId}
          listingId={activeListing.listingId}
          sourceUrl={activeListing.sourceUrl}
          lat={activeListing.lat}
          lng={activeListing.lng}
          overpricedPct={activeListing.overpricedPct}
          thumbnailUrl={activeListing.thumbnailUrl}
          onClose={() => setActiveListing(null)}
          variant="overlay"
        />
      )}

    </div>
  );
}
